import { useCallback, useEffect, useReducer, useRef } from 'react';
import { HandleNewMeasurements, InstanceFields } from '../types';
import { generateSizes } from '../utils';
import { useLazyRef } from './useLazyRef';

export interface UseDynamicSizingProps {
  itemCount: number;
  estimatedItemSize: number;
  scrollOffset: number;
}

/**
 * Hook for managing item sizing within the Dynamic List.
 */
export const useDynamicSizing = ({
  itemCount,
  estimatedItemSize,
  scrollOffset,
}: UseDynamicSizingProps) => {
  const [, forceUpdate] = useReducer((state: number) => state + 1, 0);
  const debounceForceUpdateID = useRef<number | null>(null);
  const sizingFields = useLazyRef<InstanceFields>(() => ({
    sizes: generateSizes({ estimatedItemSize, itemCount: 0 }),
    hasNewMeasurements: false,
  }));

  const setSizeValues = useCallback(() => {
    const { sizes } = sizingFields.current;
    const newSizes = generateSizes({
      itemCount,
      sizes,
      estimatedItemSize,
    });
    sizingFields.current.sizes = newSizes;
  }, [estimatedItemSize, itemCount, sizingFields]);

  const clearDebounceForceUpdate = () => {
    if (debounceForceUpdateID.current !== null) {
      clearTimeout(debounceForceUpdateID.current);
      debounceForceUpdateID.current = null;
    }
  };

  const debounceForceUpdate = useCallback(() => {
    if (debounceForceUpdateID.current === null) {
      debounceForceUpdateID.current = window.setTimeout(() => {
        debounceForceUpdateID.current = null;
        setSizeValues();
        forceUpdate();
      }, 1);
    }
  }, [setSizeValues]);

  const handleNewMeasurements: HandleNewMeasurements = useCallback(
    (index: number, newSize: number, isFirstMeasureAfterMounting: boolean) => {
      const { sizes } = sizingFields.current;

      const oldSize = sizes.getEffectiveSize(index);
      sizes.setSize(index, newSize);
      if (oldSize === newSize) {
        return;
      }

      if (isFirstMeasureAfterMounting) {
        sizingFields.current.hasNewMeasurements = true;
      } else {
        debounceForceUpdate();
      }
    },
    [debounceForceUpdate, sizingFields],
  );

  useEffect(() => {
    if (sizingFields.current.hasNewMeasurements) {
      sizingFields.current.hasNewMeasurements = false;
      debounceForceUpdate();
    }
  }, [debounceForceUpdate, scrollOffset, sizingFields]);

  useEffect(() => clearDebounceForceUpdate, []);

  return {
    handleNewMeasurements,
    debounceForceUpdate,
    sizingFields: sizingFields.current,
    setSizeValues,
  };
};
